function RouteEstimate({ route, t }) {
  return (
    <div className="mb-6 rounded-lg border border-white/10 bg-secondary/50 p-4" data-testid="route-estimate">
      <p className="text-xs uppercase tracking-luxe text-muted-foreground mb-2">{t("booking.estimatedTrip")}</p>
      <div className="flex justify-between text-sm">
        <span className="text-white/80">{route.km} km</span>
        <span className="text-white/80">≈ {route.min} {t("booking.minDrive")}</span>
      </div>
    </div>
  );
}

function EngineBreakdown({ breakdown, hold, timeLeft, t }) {
  return (
    <div className="space-y-2.5" data-testid="price-breakdown">
      <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t("booking.subtotal")}</span><span className="text-white">${breakdown.subtotal}</span></div>
      <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t("booking.tax")}</span><span className="text-white">${breakdown.tax}</span></div>
      {breakdown.gratuity > 0 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t("booking.gratuity")}</span><span className="text-white">${breakdown.gratuity}</span></div>}
      <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t("booking.total")}</span><span className="text-white" data-testid="breakdown-total">${breakdown.total}</span></div>
      <div className="h-px bg-white/10 my-3" />
      <div className="flex justify-between"><span className="text-white">{t("booking.depositNow")}</span><span className="text-white font-serif text-2xl" data-testid="breakdown-deposit">${breakdown.deposit_amount}</span></div>
      <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t("booking.balanceLater")}</span><span className="text-white/80">${breakdown.balance_due}</span></div>
      {hold && timeLeft != null && (
        <div className="mt-4 rounded-lg border border-white/20 bg-white/5 p-3 text-center" data-testid="hold-countdown">
          <p className="text-xs text-muted-foreground">{t("booking.vehicleHeld")}</p>
          <p className="text-white font-serif text-xl">{Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, "0")}</p>
        </div>
      )}
    </div>
  );
}

function TransitEstimate({ estimate, t }) {
  return (
    <div className="space-y-3">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">{t("detail.estimatedTotal")}</span>
        <span className="text-white" data-testid="estimate-total">${estimate.estimated_price}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">Deposit (25%)</span>
        <span className="text-white" data-testid="estimate-deposit">${estimate.deposit_amount}</span>
      </div>
      <div className="h-px bg-white/10 my-3" />
      <div className="flex justify-between">
        <span className="text-white">{t("detail.dueToday")}</span>
        <span className="text-white font-serif text-2xl">${estimate.deposit_amount}</span>
      </div>
    </div>
  );
}

function QuoteNote({ testId, title, note }) {
  return (
    <div className="rounded-lg border border-white/10 bg-secondary/50 p-4" data-testid={testId}>
      <p className="text-white text-sm font-medium mb-1">{title}</p>
      <p className="text-xs text-muted-foreground leading-relaxed">{note}</p>
    </div>
  );
}

export function EstimateSidebar({ isTransit, selectedService, estimate, form, config, route, breakdown, isQuoteMode, hold, timeLeft, t }) {
  const hasStandardEstimate = !isTransit && !breakdown && !isQuoteMode && estimate && estimate.estimated_price != null;
  const hasTransitEstimate = isTransit && estimate && estimate.estimated_price != null;
  return (
    <div className="rounded-lg border border-white/10 bg-card p-8 sticky top-28" data-testid="estimate-panel">
      <p className="text-xs uppercase tracking-luxe text-muted-foreground mb-6">{t("booking.estimate")}</p>
      {!selectedService ? (
        <p className="text-muted-foreground text-sm">{t("booking.selectService")}</p>
      ) : (
        <>
          <h3 className="font-serif text-2xl text-white mb-1">{isTransit ? t("svc.transit_bus.title") : t(`svc.${form.service_type}.title`)}</h3>
          <p className="text-white/50 text-sm">{isTransit ? (estimate?.vehicle || "Select a package") : selectedService.vehicle}</p>
          <p className="text-muted-foreground text-sm mb-6">
            {isTransit
              ? `${form.passengers} passengers`
              : `${form.hours} ${t("booking.hoursLabel")} · ${form.passengers} ${t("booking.passengersLabel")}`}
          </p>

          {isTransit && form.package_id && (
            <div className="mb-6 rounded-lg border border-white/10 bg-secondary/50 p-4">
              <p className="text-xs uppercase tracking-luxe text-muted-foreground mb-1">{t("booking.package")}</p>
              <p className="text-white/90 text-sm">
                {(config.transit_packages || []).find((p) => p.id === form.package_id)?.label}
              </p>
            </div>
          )}

          {form.service_type === "private_driver" && route && <RouteEstimate route={route} t={t} />}

          {!isTransit && breakdown && !isQuoteMode && (
            <EngineBreakdown breakdown={breakdown} hold={hold} timeLeft={timeLeft} t={t} />
          )}

          {!isTransit && isQuoteMode && (
            <QuoteNote testId="quote-mode-note" title={t("booking.customQuote")} note={t("booking.customQuoteNote")} />
          )}

          {hasStandardEstimate && (
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t("booking.startingFrom")}</span>
                <span className="text-white" data-testid="estimate-total">${estimate.estimated_price}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{t("booking.selectVehicleHint")}</p>
            </div>
          )}

          {hasTransitEstimate && <TransitEstimate estimate={estimate} t={t} />}

          {isTransit && estimate && estimate.quote_required && (
            <QuoteNote testId="quote-required-note" title={t("booking.priceOnRequest")} note={t("booking.priceOnRequestNote")} />
          )}
        </>
      )}
    </div>
  );
}
